import React, { useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Card, CardBody, Badge, Spinner } from "reactstrap";
import { GripVertical, Save, X } from "lucide-react";
import { useMenuStore } from "../../../stores/Assets/menu";
import { axiosHelper, tokenHelper } from "../../../stores/helpers";

const MenuOrderingModal = ({ isOpen, toggle, menus, siteId }) => {
  const { isLoading } = useMenuStore((s) => s);
  const initial = useMemo(() => {
    const sorted = [...(menus || [])];
    return sorted.map((m, idx) => ({
      menu_id: m.menu_id,
      name: m.name,
      name_ar: m.name_ar,
      created_at: m.created_at,
      ordering: idx + 1,
    }));
  }, [menus]);
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverItem, setDraggedOverItem] = useState(null);

  const move = (index, dir) => {
    const next = [...items];
    const swapWith = index + dir;
    if (swapWith < 0 || swapWith >= next.length) return;
    const tmp = next[index];
    next[index] = next[swapWith];
    next[swapWith] = tmp;
    // re-number
    const renum = next.map((it, i) => ({ ...it, ordering: i + 1 }));
    setItems(renum);
  };

  const onSave = async () => {
    try {
      setSaving(true);
      const payload = { menuIds: items.map(it => it.menu_id) };
      const token = await tokenHelper.getToken();
      await axiosHelper.patch(`/site/${siteId}/menus/ordering`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toggle();
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(item.menu_id));
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedOverItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e, item) => {
    e.preventDefault();
    setDraggedOverItem(item);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDraggedOverItem(null);
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    const draggedId = draggedItem?.menu_id;
    const targetId = targetItem?.menu_id;
    if (!draggedItem || draggedId === targetId) return;
    const draggedIndex = items.findIndex((it) => it.menu_id === draggedId);
    const targetIndex = items.findIndex((it) => it.menu_id === targetId);
    if (draggedIndex === -1 || targetIndex === -1) return;
    const next = [...items];
    const [dragData] = next.splice(draggedIndex, 1);
    next.splice(targetIndex, 0, dragData);
    const renum = next.map((it, i) => ({ ...it, ordering: i + 1 }));
    setItems(renum);
    setDraggedOverItem(null);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" scrollable>
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center gap-2">
          <GripVertical size={20} />
          Reorder Site Menus
        </div>
      </ModalHeader>

      <ModalBody style={{ overflowY: "auto" }}>
        <div className="space-y-0">
          {items.map((item) => (
            <div key={item.menu_id} className="w-100 mb-2">
              <Card
                className={`h-100 mb-0 ${
                  draggedItem?.menu_id === item.menu_id
                    ? "opacity-50"
                    : draggedOverItem?.menu_id === item.menu_id
                    ? "border-primary border-2"
                    : "hover-shadow"
                }`}
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, item)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, item)}
                style={{ cursor: "move" }}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <GripVertical size={16} className="text-muted" />
                      <Badge color="primary" style={{ fontSize: "12px" }}>
                        #{initial.find(init => init.menu_id === item.menu_id)?.ordering}
                      </Badge>
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                      <div className="flex-grow-1">
                        <h6 className="mb-0" title={item.name}>
                          {item.name}
                        </h6>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          ))}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving || isLoading}>
          <X size={16} className="me-1" />
          Cancel
        </Button>
        <Button color="primary" onClick={onSave} disabled={saving || isLoading}>
          {saving ? (
            <>
              <Spinner size="sm" className="me-1" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} className="me-1" />
              Save Order
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MenuOrderingModal;



